import EditIcon from "@mui/icons-material/Edit";
import { Box, Grid, IconButton, Tooltip } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import {
  DEBOUNCE_DELAY,
  DEFAULT_PAGE_SIZE,
} from "src/constants/globalConstants";
import { DataTable } from "src/generic-ui/DataTable";
import Switch from "src/generic-ui/Switch";
import { useDebounce } from "src/hooks/useDebounce";
import { logger } from "src/services";
import { addressService } from "src/services/address.service";
import { IArea } from "src/types";
import Sidebar from "./Sidebar";
import withPermission from "src/components/permission/withPermission";
import { permissions } from "src/constants/permissions";
import { Helmet } from "react-helmet-async";
import ActionBar from "src/generic-ui/ActionBar";
import { useWarehouse } from "src/hooks/address/useWarehouse";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import FilterSidebar from "./FilterSidebar";

const ColValue = ({ value = "" }: { value: any }) => {
  return (
    <Box display="flex" borderRadius="4px">
      {value}
    </Box>
  );
};
function Warehouse() {
  const [isSidebarVisible, setVisible] = useState<boolean>(false);
  const [isFilterModalVisible, setFilterModal] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState<number>(0);
  const [selectedId, setSelected] = useState<string | null>(null);
  const debouncedSearchTerm = useDebounce({
    searchQuery: searchTerm,
    delay: DEBOUNCE_DELAY,
  });

  const { isLoading, warehouses, mutate, error, meta } = useWarehouse({
    limit: pageSize,
    page: page + 1,
    searchTerm: debouncedSearchTerm,
    city: city,
  });
  const onToggleSideBar = () => setVisible((prev) => !prev);

  const [rowCountState, setRowCountState] = useState(meta?.total || 0);

  useEffect(() => {
    setRowCountState((prevRowCountState) =>
      meta?.total !== undefined ? meta?.total : prevRowCountState
    );
  }, [meta, setRowCountState]);

  const onClose = () => {
    onToggleSideBar();
    setSelected(null);
  };

  const handleOnCellClick = ({ row }: { row: IArea }) => {
    setSelected(row.id);
    onToggleSideBar();
  };

  const onRefresh = () => {
    mutate();
  };

  const updateElement = async ({
    keyName,
    id,
    value,
  }: {
    keyName: string;
    id: string;
    value: any;
  }) => {
    try {
      await addressService.updateArea({ id, [keyName]: value });
    } catch (error) {
      logger.error(error);
    } finally {
      onRefresh();
    }
  };

  const columns: any = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
    },

    {
      field: "city",
      headerName: "City",
      renderCell: ({ row }: { row: any }) => (
        <ColValue value={row.city.title} />
      ),
    },
    {
      field: "emails",
      headerName: "Emails",
      renderCell: ({ row }: { row: any }) => {
        const data = row.emails.split(",");
        return (
          <div className="">
            {data.map((el, index) => (
              <div key={index} className="border px-3 py-1 my-2 rounded-md">
                {el}
              </div>
            ))}
          </div>
        );
      },
      flex: 2,
    },
    {
      field: "isActive",
      headerName: "Active",
      flex: 1,
      renderCell: ({ row }: { row: any }) => (
        <Switch
          isChecked={!!row.isActive}
          showPopOver={true}
          onClick={(isChecked: boolean) =>
            updateElement({ keyName: "isActive", value: isChecked, id: row.id })
          }
        />
      ),
    },

    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: ({ row }: { row: any }) => (
        <IconButton
          onClick={() => {
            handleOnCellClick({ row });
          }}
        >
          <EditIcon />
        </IconButton>
      ),
    },
  ];
  if (error) {
    return <div>Something Went Wrong</div>;
  }

  const onToggleFilter = () => setFilterModal((prev) => !prev);
  const onCloseFilterModal = () => {
    onToggleFilter();
  };

  const onReset = () => {
    setCity("");
    setSearchTerm("");
  };

  const showResetAllButton = useMemo(() => {
    return !!debouncedSearchTerm || !!city;
  }, [debouncedSearchTerm, city]);

  const filterConfigs = { city, setCity, onReset, showResetAllButton };

  return (
    <>
      <Helmet>
        <title>AAAF LTD - warehouse</title>
      </Helmet>

      <ActionBar title="Warehouses">
        <div className="flex">
          <input
            value={searchTerm}
            placeholder="Search"
            onChange={(e) => setSearchTerm(e.target.value)}
            className=" block  rounded-md border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400  sm:text-sm sm:leading-6"
          />
          <Tooltip arrow title="filter options">
            <IconButton
              className="bg-indigo-500 hover:bg-indigo-600 text-white ml-2 p-1.5"
              onClick={onToggleFilter}
            >
              {showResetAllButton && (
                <div
                  style={{
                    position: "absolute",
                    top: "-0.2rem",
                    right: "-0.2rem",
                    borderRadius: "50%",
                    background: "red",
                    width: "0.7rem",
                    height: "0.7rem",
                  }}
                />
              )}
              <FilterListIcon fontSize="medium" className="text-white" />
            </IconButton>
          </Tooltip>

          {showResetAllButton && (
            <Tooltip arrow title="reset filter">
              <IconButton
                className="bg-red-500 hover:bg-red-600 text-white ml-2 p-1.5"
                onClick={() => {
                  onReset();
                }}
              >
                <RestartAltIcon fontSize="medium" className="text-white" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </ActionBar>
      <DataTable
        columns={columns}
        data={warehouses}
        isLoading={isLoading}
        page={page}
        pageSize={pageSize}
        disableColumnMenu={true}
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        rowCountState={rowCountState}
      />
      {isSidebarVisible && (
        <Sidebar
          isVisible={isSidebarVisible}
          onClose={onClose}
          selectedId={selectedId}
          onRefresh={onRefresh}
        />
      )}
      {isFilterModalVisible && (
        <FilterSidebar
          {...filterConfigs}
          isVisible={true}
          onClose={onCloseFilterModal}
        />
      )}
    </>
  );
}

export default withPermission(
  Warehouse,
  permissions["location"].permissionSet.warehouse.view
);
